import { useState, useMemo, useEffect, useLayoutEffect, useRef, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react'
import {
  Check, ChevronDown, ChevronRight, FileText, FolderTree, Globe,
  Lock, Network, Pencil, Plus, Star, Trash2, User as UserIcon, Users,
} from 'lucide-react'
import type { ExplorerNode, DefaultParents } from './types.ts'
import { DEFAULT_TARGET_KEYS, DEFAULT_TARGET_LABELS, SHORT_BADGE } from './types.ts'
import type { GroupType } from '../types.ts'
import { GROUP_TYPE_LABELS, GROUP_TYPES } from '../types.ts'

const DOMAIN_ROOT_KEY = '__domain_root__'

export interface ExplorerActions {
  /** Verhindert das Oeffnen des Kontextmenues komplett (Seitenweites Lese-Flag) */
  canEdit?: boolean
  /** Sub-OU unter parentId anlegen (null = Domain-Root) */
  onAddOu?: (parentId: string | null) => void
  /** Gruppe anlegen (zeigt Typ-Submenu) – nur GlobalStandards */
  onAddGroup?: (ouId: string, groupType: GroupType) => void
  /** User anlegen – nur GlobalStandards */
  onAddUser?: (ouId: string) => void
  /**
   * Umbenennen. Welche Knoten umbenennbar sind, steuert renameableKinds.
   * Default: ou, group, user (alle).
   */
  onRename?: (node: ExplorerNode) => void
  renameableKinds?: ReadonlyArray<'ou' | 'group' | 'user'>
  /** Beschreibung bearbeiten – nur GlobalStandards */
  onEditDescription?: (node: ExplorerNode) => void
  /** Loeschen */
  onDelete?: (node: ExplorerNode) => void
  /** Doppelklick-Handler (zeigt Tooltip-Hinweis wenn gesetzt) */
  onDoubleClick?: (node: ExplorerNode) => void
  /** Fuer "Als Standard setzen fuer…"-Submenu */
  defaultParents?: DefaultParents
  onToggleDefault?: (ouId: string, target: keyof DefaultParents) => void
}

interface Props {
  nodes: ExplorerNode[]
  domainLabel: string
  domainSubLabel?: string
  title: string
  ouCount: number
  groupCount: number
  userCount: number
  isLoading?: boolean
  emptyMessage?: string
  hint?: string
  actions?: ExplorerActions
}

export function StructureTreeExplorer({
  nodes, domainLabel, domainSubLabel, title,
  ouCount, groupCount, userCount,
  isLoading, emptyMessage, hint,
  actions = {},
}: Props) {
  const {
    canEdit = true,
    onAddOu, onAddGroup, onAddUser,
    onRename, renameableKinds = ['ou', 'group', 'user'],
    onEditDescription, onDelete, onDoubleClick,
    defaultParents = {}, onToggleDefault,
  } = actions

  const [filter, setFilter] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [contextMenu, setContextMenu] = useState<{ node: ExplorerNode | null; x: number; y: number } | null>(null)
  const contextMenuRef = useRef<HTMLDivElement | null>(null)
  const submenuTriggerRef = useRef<HTMLDivElement | null>(null)
  const [submenuOpen, setSubmenuOpen] = useState(false)
  const [groupSubmenuOpen, setGroupSubmenuOpen] = useState(false)
  const [submenuDir, setSubmenuDir] = useState<{ horiz: 'right' | 'left'; vert: 'down' | 'up' }>({
    horiz: 'right', vert: 'down',
  })

  useLayoutEffect(() => {
    if (!submenuOpen || !submenuTriggerRef.current) return
    const rect = submenuTriggerRef.current.getBoundingClientRect()
    const submenuH = DEFAULT_TARGET_KEYS.length * 32 + 8
    setSubmenuDir({
      horiz: rect.right + 248 > window.innerWidth ? 'left' : 'right',
      vert: rect.top + submenuH + 8 > window.innerHeight ? 'up' : 'down',
    })
  }, [submenuOpen])

  const filteredNodes = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return nodes
    function filterNode(n: ExplorerNode): ExplorerNode | null {
      const selfMatch = n.name.toLowerCase().includes(q)
      const keptChildren = n.children.map(filterNode).filter((x): x is ExplorerNode => x !== null)
      if (selfMatch || keptChildren.length > 0) return { ...n, children: keptChildren }
      return null
    }
    return nodes.map(filterNode).filter((x): x is ExplorerNode => x !== null)
  }, [nodes, filter])

  useEffect(() => {
    if (!contextMenu) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (target && contextMenuRef.current?.contains(target)) return
      setContextMenu(null)
      setSubmenuOpen(false)
      setGroupSubmenuOpen(false)
    }
    window.addEventListener('mousedown', handler)
    window.addEventListener('contextmenu', handler)
    return () => {
      window.removeEventListener('mousedown', handler)
      window.removeEventListener('contextmenu', handler)
    }
  }, [contextMenu])

  function toggleCollapse(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function handleContextMenu(e: ReactMouseEvent, node: ExplorerNode | null) {
    e.preventDefault()
    e.stopPropagation()
    if (!canEdit) return
    setContextMenu({ node, x: e.clientX, y: e.clientY })
    setSubmenuOpen(false)
    setGroupSubmenuOpen(false)
  }

  function closeMenu() {
    setContextMenu(null)
    setSubmenuOpen(false)
    setGroupSubmenuOpen(false)
  }

  // ─── Badge Rendering ───────────────────────────────────────────────────────

  function renderOuBadges(node: ExplorerNode) {
    const badges: ReactNode[] = []

    if (node.ouKind === 'customer-root') {
      badges.push(
        <span key="cr" className="text-[10px] font-medium text-brand-600 bg-brand-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
          Kunden-Root
        </span>,
      )
    } else if (node.ouKind === 'systemhaus') {
      badges.push(
        <span key="sh" className="text-[10px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
          Systemhaus
        </span>,
      )
    } else if (node.ouKind === 'mandatory') {
      badges.push(
        <span key="ma" className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
          Pflicht
        </span>,
      )
    } else if (node.isReadOnly) {
      badges.push(
        <span key="gl" className="text-[10px] font-medium text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
          Global
        </span>,
      )
    }

    if (node.defaultTargets && node.defaultTargets.length > 0) {
      badges.push(
        <span
          key="dt"
          className="flex items-center gap-1 flex-shrink-0"
          title={`Default-Speicherort für: ${node.defaultTargets.map((t) => DEFAULT_TARGET_LABELS[t]).join(', ')}`}
        >
          {node.defaultTargets.map((t) => (
            <span key={t} className="text-[10px] font-mono text-brand-700 bg-brand-50 border border-brand-200 px-1.5 py-0.5 rounded flex-shrink-0">
              ★ {SHORT_BADGE[t]}
            </span>
          ))}
        </span>,
      )
    }

    return badges.length > 0 ? <>{badges}</> : null
  }

  // ─── Node Rendering ────────────────────────────────────────────────────────

  function renderNode(node: ExplorerNode, depth: number): ReactNode {
    const hasChildren = node.children.length > 0
    const isCollapsedNode = collapsed.has(node.key)

    const Icon =
      node.kind === 'ou' ? Network :
      node.kind === 'group' ? Users :
      node.kind === 'user' ? UserIcon : FolderTree

    const iconColor =
      node.kind === 'orphan' ? 'text-amber-500' :
      node.kind !== 'ou' ? 'text-gray-400' :
      node.ouKind === 'customer-root' ? 'text-brand-500' :
      node.ouKind === 'systemhaus' ? 'text-green-500' :
      node.ouKind === 'mandatory' ? 'text-indigo-500' : 'text-green-500'

    return (
      <div key={node.key}>
        <div
          className="flex items-center gap-1 py-1.5 hover:bg-gray-50 border-b border-gray-100 group cursor-default select-none"
          style={{ paddingLeft: `${8 + depth * 20}px` }}
          onContextMenu={(e) => handleContextMenu(e, node)}
          onDoubleClick={() => node.kind !== 'orphan' && onDoubleClick?.(node)}
          title={onDoubleClick && node.kind !== 'orphan' ? 'Doppelklick für Einstellungen · Rechtsklick für Optionen' : undefined}
        >
          <button
            className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded"
            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleCollapse(node.key) }}
            tabIndex={-1}
          >
            {hasChildren ? (
              isCollapsedNode
                ? <ChevronRight className="w-3.5 h-3.5" />
                : <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <span className="w-3.5 h-3.5 inline-block" />
            )}
          </button>

          <Icon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />

          <span
            className={`text-sm px-1 flex-shrink-0 ${
              node.kind === 'orphan' ? 'text-amber-700 italic' :
              node.isReadOnly ? 'text-gray-600' : 'text-gray-800'
            } ${onDoubleClick ? 'font-mono' : ''}`}
            title={node.description ?? undefined}
          >
            {node.name}
          </span>

          {node.kind === 'group' && node.groupType && (
            <span className="text-[10px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
              {node.groupType}
            </span>
          )}

          {node.kind === 'ou' && renderOuBadges(node)}

          {node.kind !== 'ou' && node.isReadOnly && (
            <span className="text-[10px] font-medium text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1">
              <Lock className="w-2.5 h-2.5" />
              Global
            </span>
          )}

          {node.description && node.kind !== 'orphan' && onDoubleClick && (
            <span className="text-[11px] text-gray-400 hidden md:inline min-w-0 flex-1 truncate">
              {node.description}
            </span>
          )}
        </div>

        {!isCollapsedNode && hasChildren && (
          <div>{node.children.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    )
  }

  // ─── Domain Root ───────────────────────────────────────────────────────────

  function renderDomainRoot() {
    const hasChildren = filteredNodes.length > 0
    const isCollapsedRoot = collapsed.has(DOMAIN_ROOT_KEY)
    return (
      <div>
        <div
          className="flex items-center gap-1 px-2 py-2 bg-gray-50 border-b border-gray-200 cursor-default select-none"
          onContextMenu={(e) => handleContextMenu(e, null)}
        >
          <button
            className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700 rounded"
            onClick={() => toggleCollapse(DOMAIN_ROOT_KEY)}
            tabIndex={-1}
          >
            {hasChildren ? (
              isCollapsedRoot
                ? <ChevronRight className="w-3.5 h-3.5" />
                : <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <span className="w-3.5 h-3.5 inline-block" />
            )}
          </button>
          <Globe className="w-4 h-4 flex-shrink-0 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 font-mono px-1">{domainLabel}</span>
          {domainSubLabel && <span className="text-[10px] text-gray-400">{domainSubLabel}</span>}
        </div>
        {!isCollapsedRoot && hasChildren && (
          <div>{filteredNodes.map((n) => renderNode(n, 1))}</div>
        )}
      </div>
    )
  }

  // ─── Context Menu ──────────────────────────────────────────────────────────

  function renderContextMenu() {
    if (!contextMenu) return null
    const { node } = contextMenu
    const isRoot = node === null
    const isOu = !isRoot && node!.kind === 'ou'
    const isOrphan = !isRoot && node!.kind === 'orphan'
    const readOnly = !isRoot && !!node!.isReadOnly
    const nodeKind = node?.kind as 'ou' | 'group' | 'user' | undefined
    const canRename = !isRoot && !isOrphan && !!onRename && nodeKind && renameableKinds.includes(nodeKind) && !readOnly
    const canDelete = !isRoot && !isOrphan && !!onDelete && !readOnly

    const safeX = Math.min(contextMenu.x, window.innerWidth - 216)
    const safeY = Math.min(contextMenu.y, window.innerHeight - 300)

    return (
      <div
        ref={contextMenuRef}
        className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        style={{ left: safeX, top: safeY, minWidth: 200 }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Sub-OU anlegen */}
        {(isRoot || isOu) && onAddOu && (
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            onClick={() => { onAddOu(isRoot ? null : node!.id); closeMenu() }}
          >
            <Plus className="w-3.5 h-3.5" />
            Sub-OU anlegen
          </button>
        )}

        {/* Gruppe anlegen (GlobalStandards) */}
        {isOu && onAddGroup && (
          <div
            className="relative"
            onMouseEnter={() => { setGroupSubmenuOpen(true); setSubmenuOpen(false) }}
            onMouseLeave={() => setGroupSubmenuOpen(false)}
          >
            <button
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              onClick={() => setGroupSubmenuOpen((v) => !v)}
            >
              <Users className="w-3.5 h-3.5" />
              <span className="flex-1">Gruppe anlegen…</span>
              <ChevronRight className="w-3 h-3 text-gray-400" />
            </button>
            {groupSubmenuOpen && (
              <div className="absolute left-full top-0 -ml-px bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[220px]">
                {GROUP_TYPES.map((t) => (
                  <button
                    key={t}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => { onAddGroup(node!.id, t); closeMenu() }}
                  >
                    <span className="font-mono text-xs">{t} – {GROUP_TYPE_LABELS[t]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User anlegen */}
        {isOu && onAddUser && (
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            onClick={() => { onAddUser(node!.id); closeMenu() }}
          >
            <UserIcon className="w-3.5 h-3.5" />
            User anlegen
          </button>
        )}

        {/* Umbenennen */}
        {canRename && (
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            onClick={() => { onRename!(node!); closeMenu() }}
          >
            <Pencil className="w-3.5 h-3.5" />
            Umbenennen
          </button>
        )}
        {!isRoot && !isOrphan && !!onRename && nodeKind && renameableKinds.includes(nodeKind) && readOnly && (
          <button
            disabled
            title="Schreibgeschütztes Element kann nicht umbenannt werden"
            className="w-full text-left px-3 py-1.5 text-sm text-gray-300 cursor-not-allowed flex items-center gap-2"
          >
            <Pencil className="w-3.5 h-3.5" />
            Umbenennen
          </button>
        )}

        {/* Beschreibung bearbeiten (GlobalStandards) */}
        {!isRoot && !isOrphan && onEditDescription && (
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            onClick={() => { onEditDescription(node!); closeMenu() }}
          >
            <FileText className="w-3.5 h-3.5" />
            Beschreibung bearbeiten
          </button>
        )}

        {/* Als Standard setzen fuer… */}
        {isOu && onToggleDefault && (
          <div
            ref={submenuTriggerRef}
            className="relative"
            onMouseEnter={() => { setSubmenuOpen(true); setGroupSubmenuOpen(false) }}
            onMouseLeave={() => setSubmenuOpen(false)}
          >
            <button
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              onClick={() => setSubmenuOpen((v) => !v)}
            >
              <Star className="w-3.5 h-3.5" />
              <span className="flex-1">Als Standard setzen für…</span>
              <ChevronRight className="w-3 h-3 text-gray-400" />
            </button>
            {submenuOpen && (
              <div
                className={`absolute bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[220px] ${
                  submenuDir.horiz === 'right' ? 'left-full -ml-px' : 'right-full -mr-px'
                } ${submenuDir.vert === 'down' ? 'top-0' : 'bottom-0'}`}
              >
                {DEFAULT_TARGET_KEYS.map((key) => {
                  const isCurrent = defaultParents[key] === node!.id
                  return (
                    <button
                      key={key}
                      className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                        isCurrent ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => { onToggleDefault(node!.id, key); closeMenu() }}
                    >
                      <span className="w-4 inline-flex items-center justify-center">
                        {isCurrent && <Check className="w-3.5 h-3.5" />}
                      </span>
                      <span className="font-mono text-xs">{DEFAULT_TARGET_LABELS[key]}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Loeschen */}
        {canDelete && (
          <>
            <div className="border-t border-gray-100 my-1" />
            <button
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              onClick={() => { onDelete!(node!); closeMenu() }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Löschen
            </button>
          </>
        )}
        {!isRoot && !isOrphan && !!onDelete && readOnly && (
          <>
            <div className="border-t border-gray-100 my-1" />
            <button
              disabled
              title="Schreibgeschütztes Element kann nicht gelöscht werden"
              className="w-full text-left px-3 py-1.5 text-sm text-gray-300 cursor-not-allowed flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Löschen
            </button>
          </>
        )}
      </div>
    )
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtern (OU / Gruppe / User) …"
          className="flex-1 min-w-[240px] px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        />
        {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <span className="text-[11px] text-gray-500">
            {ouCount} OUs · {groupCount} Gruppen · {userCount} User
          </span>
        </div>
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">Lade…</p>
        ) : filteredNodes.length === 0 && !filter ? (
          <p className="px-4 py-8 text-sm text-gray-400 text-center">
            {emptyMessage ?? 'Keine Einträge vorhanden.'}
          </p>
        ) : (
          renderDomainRoot()
        )}
      </div>

      {renderContextMenu()}
    </div>
  )
}
